/**
 * Muted words — matching primitives.
 *
 * Pure and framework-free on purpose: services, React components and the test
 * script all need the same rules, and duplicating "does this text contain a
 * muted word?" is exactly how the blocking rule ended up applied inconsistently
 * across surfaces.
 *
 * Why this is matched on the client rather than in a Firestore query: Firestore
 * offers `array-contains` / `array-contains-any`, both positive membership
 * tests. Muting is a negation ("hide docs whose text matches any of my words")
 * and there is no `not-array-contains`, so no keyword-index field could express
 * it. Multi-word phrases would additionally need n-gram indexing. A compiled
 * regex over the raw text handles both, at ~1 test per rendered item.
 */

/** Words shorter than this are rejected: muting "a"/"the" would empty the feed. */
export const MIN_MUTED_WORD_LENGTH = 3;

/** Upper bound on list size. Performance is fine well past this — the cap is
 *  about keeping the list comprehensible to the person managing it. */
export const MAX_MUTED_WORDS = 100;

/** localStorage key for the cold-boot mirror. Seeded synchronously at startup so
 *  the first feed paint already knows the list, avoiding a flash of muted content
 *  before the Firestore listener delivers. */
export const MUTED_WORDS_CACHE_KEY = 'muted_words';

/**
 * Canonical form of a muted word: lowercased, diacritics stripped, internal
 * whitespace collapsed. MUST be applied to both the stored word and the text
 * being tested — normalizing only one side silently breaks accented content,
 * of which this app has plenty.
 */
export function normalizeMutedWord(input: string): string {
  return String(input ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // combining diacritical marks
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Escape regex metacharacters so muting ".*" or "(" matches literally rather
 *  than matching everything or throwing at construction. */
const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * True when the environment supports lookbehind (Safari < 16.4 does not). Probed
 * once at module load rather than per call.
 */
const SUPPORTS_LOOKBEHIND = (() => {
  try {
    // eslint-disable-next-line prefer-regex-literals
    new RegExp('(?<!x)y');
    return true;
  } catch {
    return false;
  }
})();

/**
 * Compile a mute list into ONE alternation regex, or null when nothing is muted.
 *
 * One regex rather than a per-word loop is what keeps a 100-word list cheap:
 * a single test per item instead of 100. Callers should memoize on the list.
 *
 * Boundaries use `\p{L}\p{N}_` lookarounds rather than `\b`, which is
 * ASCII-only and mis-fires against the Arabic script, emoji and accented names
 * present in this app's content. Where lookbehind is unavailable the leading
 * boundary is enforced by `matchHasLeadingBoundary` instead.
 */
export function compileMutedWordsRegex(words: string[] | null | undefined): RegExp | null {
  const cleaned = Array.from(new Set((words || [])
    .map(normalizeMutedWord)
    .filter(w => w.length >= MIN_MUTED_WORD_LENGTH)));

  if (cleaned.length === 0) return null;

  // A normalized word's spaces are single, so " " → "\s+" lets "gold  rush"
  // still match "gold rush".
  const alternation = cleaned
    .map(w => escapeRegExp(w).replace(/ /g, '\\s+'))
    .join('|');

  const trailing = '(?![\\p{L}\\p{N}_])';

  if (SUPPORTS_LOOKBEHIND) {
    // NOTE: no `g` flag. A global regex carries `lastIndex` between `.test()`
    // calls and would return alternating results down a list of items.
    return new RegExp(`(?<![\\p{L}\\p{N}_])(?:${alternation})${trailing}`, 'iu');
  }

  // Without lookbehind the leading boundary can't be expressed, so the scan has
  // to walk every candidate match and check the preceding character by hand —
  // which needs the `g` flag. textIsMuted resets lastIndex before each use.
  return new RegExp(`(?:${alternation})${trailing}`, 'giu');
}

const WORD_CHAR = /[\p{L}\p{N}_]/u;

/**
 * True when `text` contains a muted word. `re` comes from
 * compileMutedWordsRegex; null means nothing is muted.
 */
export function textIsMuted(text: string | null | undefined, re: RegExp | null): boolean {
  if (!re || !text) return false;
  const normalized = normalizeMutedWord(text);

  if (SUPPORTS_LOOKBEHIND) return re.test(normalized);

  // Fallback: the compiled regex enforces only the trailing boundary, so keep
  // scanning until a match also has a clean leading boundary. Stopping at the
  // first match would miss "gold" in "marigold gold" — the "gold" inside
  // "marigold" matches first and is correctly rejected, but a real hit follows.
  re.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(normalized)) !== null) {
    if (m.index === 0 || !WORD_CHAR.test(normalized[m.index - 1])) {
      re.lastIndex = 0;
      return true;
    }
    // Zero-length matches are impossible here (every alternative has ≥1 char),
    // so exec always advances and this terminates.
  }
  re.lastIndex = 0;
  return false;
}

/**
 * True when a yap / shout / group post carries a muted word.
 *
 * Checks all three text-bearing keys for the same reason the blocked-author
 * check inspects several id keys: a shout keeps its own caption AND embeds the
 * original yap, so testing only `content` would let muted text through whenever
 * it was quoted rather than authored.
 */
export function itemIsMuted(item: any, re: RegExp | null): boolean {
  if (!re || !item) return false;
  return textIsMuted(item.content, re)
    || textIsMuted(item.shoutCaption, re)
    || textIsMuted(item.originalYap?.content, re);
}

/**
 * True when a forum/tribe should be hidden from DISCOVERY surfaces.
 *
 * Note the field is `description` — these documents have no `bio`. Groups the
 * viewer has already joined are never hidden: muting affects what gets
 * recommended, not what someone is already a member of.
 */
export function groupIsMuted(
  group: any,
  re: RegExp | null,
  isJoined?: (groupId: string) => boolean,
): boolean {
  if (!re || !group) return false;
  if (isJoined && group.id != null && isJoined(String(group.id))) return false;
  return textIsMuted(group.name, re) || textIsMuted(group.description, re);
}

/**
 * Very common words that pass the length check but would empty a feed. The
 * median yap is ~86 characters / ~15 words, so muting one of these hides most of
 * everything — and the user is unlikely to connect an empty feed back to this
 * screen. These are warned about and require confirmation rather than being
 * blocked outright: it is still the user's choice.
 */
const COMMON_WORDS = new Set([
  'the', 'and', 'for', 'you', 'are', 'but', 'not', 'was', 'his', 'her', 'she',
  'him', 'they', 'them', 'this', 'that', 'with', 'have', 'has', 'had', 'from',
  'what', 'when', 'who', 'why', 'how', 'all', 'any', 'can', 'get', 'got', 'out',
  'about', 'just', 'like', 'now', 'one', 'our', 'their', 'there', 'here', 'been',
  'would', 'could', 'should', 'will', 'yap', 'yaps',
]);

/** True when muting this word would very likely empty the user's feed. */
export function isCommonWord(raw: string): boolean {
  return COMMON_WORDS.has(normalizeMutedWord(raw));
}

/**
 * Validate a candidate word for the settings UI. Returns null when acceptable,
 * otherwise a message suitable for display.
 */
export function validateMutedWord(raw: string, existing: string[]): string | null {
  const word = normalizeMutedWord(raw);
  if (!word) return 'Enter a word or phrase to mute.';
  if (word.length < MIN_MUTED_WORD_LENGTH) {
    return `Muted words must be at least ${MIN_MUTED_WORD_LENGTH} characters.`;
  }
  if (existing.map(normalizeMutedWord).includes(word)) return 'That word is already muted.';
  if (existing.length >= MAX_MUTED_WORDS) {
    return `You can mute up to ${MAX_MUTED_WORDS} words.`;
  }
  return null;
}
